import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { X, Check, Loader2, Download } from 'lucide-react';
import { useImageEditor } from './useImageEditor';
import { ImageEditorToolbar } from './ImageEditorToolbar';
import { AnnotationRenderer } from './AnnotationRenderer';
import type { ImageFeedbackEditorProps } from './types';
import './imageEditor.css';

// Cursor mapping per tool
const CURSOR_MAP: Record<string, string> = {
  hand: 'grab',
  move: 'move',
  pencil: 'crosshair',
  highlighter: 'crosshair',
  eraser: 'cell',
  rect: 'crosshair',
  circle: 'crosshair',
  line: 'crosshair',
  arrow: 'crosshair',
  text: 'text',
  pin: 'copy',
  blur: 'crosshair',
  censor: 'crosshair',
};

const ImageFeedbackEditor: React.FC<ImageFeedbackEditorProps> = ({
  imageSrc,
  onSave,
  onCancel,
  onDownload,
  hidden = false,
  accentColor = '#3B82F6',
  className = '',
}) =>
{
  const editor = useImageEditor(imageSrc);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [exportError, setExportError] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [textValue, setTextValue] = useState('');

  // Focus textarea when text input appears
  useEffect(() =>
  {
    if (editor.textInput && textareaRef.current)
    {
      setTextValue('');
      setTimeout(() =>
      {
        if (textareaRef.current)
        {
          textareaRef.current.focus();
        }
      }, 0);
    }
  }, [editor.textInput]);

  const handleCreateReply = useCallback(async () =>
  {
    const base64 = await editor.exportImage();
    if (!base64)
    {
      setExportError(true);
      setTimeout(() => setExportError(false), 4000);
      return;
    }
    onSave(base64);
  }, [editor, onSave]);

  const handleDownload = useCallback(async () =>
  {
    const base64 = await editor.exportImage();
    if (!base64)
    {
      setExportError(true);
      setTimeout(() => setExportError(false), 4000);
      return;
    }
    if (onDownload)
    {
      onDownload(base64);
    }
  }, [editor, onDownload]);

  const handleCancel = useCallback(() =>
  {
    if (showCancelConfirm)
    {
      onCancel();
    }
    else
    {
      setShowCancelConfirm(true);
      setTimeout(() => setShowCancelConfirm(false), 3000);
    }
  }, [showCancelConfirm, onCancel]);

  const handleKeyDown = useCallback((e: KeyboardEvent) =>
  {
    if (e.key === 'Escape')
    {
      if (editor.textInput)
      {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        editor.cancelText();
      }
      else
      {
        handleCancel();
      }
    }
  }, [editor, handleCancel]);

  useEffect(() =>
  {
    window.addEventListener('keydown', handleKeyDown, { capture: true });
    return () => window.removeEventListener('keydown', handleKeyDown, { capture: true });
  }, [handleKeyDown]);

  // Ctrl+Wheel zoom
  useEffect(() =>
  {
    const handleWheel = (e: WheelEvent) =>
    {
      if (e.ctrlKey || e.metaKey)
      {
        e.preventDefault();
        if (e.deltaY < 0)
        {
          editor.zoomIn();
        }
        else
        {
          editor.zoomOut();
        }
      }
    };

    const container = editor.containerRef.current;
    if (container)
    {
      container.addEventListener('wheel', handleWheel, { passive: false });
      return () => container.removeEventListener('wheel', handleWheel);
    }
    return undefined;
  }, [editor]);

  const handleTextKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) =>
  {
    if (e.key === 'Enter' && !e.shiftKey)
    {
      e.preventDefault();
      editor.confirmText(textValue);
    }
    if (e.key === 'Escape')
    {
      e.preventDefault();
      e.stopPropagation();
      e.nativeEvent.stopImmediatePropagation();
      editor.cancelText();
    }
  };

  const [isPanning, setIsPanning] = React.useState(false);
  const svgCursor = isPanning && editor.tool === 'hand' ? 'grabbing' : (CURSOR_MAP[editor.tool] ?? 'default');

  const handleSvgMouseDown = useCallback((e: React.MouseEvent<SVGElement>) =>
  {
    if (editor.tool === 'hand')
    {
      setIsPanning(true);
    }
    editor.onMouseDown(e);
  }, [editor]);

  const handleSvgMouseUp = useCallback((e: React.MouseEvent<SVGElement>) =>
  {
    setIsPanning(false);
    editor.onMouseUp(e);
  }, [editor]);

  const handleSvgMouseLeave = useCallback((e: React.MouseEvent<SVGElement>) =>
  {
    setIsPanning(false);
    editor.onMouseLeave(e);
  }, [editor]);

  // Combine all annotations for rendering
  const allAnnotations = React.useMemo(() =>
  {
    const list = [...editor.annotations];
    if (editor.previewAnnotation)
    {
      // If moving, replace the selected annotation with the preview
      if (editor.selectedAnnotationIndex !== null)
      {
        list[editor.selectedAnnotationIndex] = editor.previewAnnotation;
      }
      else
      {
        // Otherwise add preview to the end
        list.push(editor.previewAnnotation);
      }
    }
    return list;
  }, [editor.annotations, editor.previewAnnotation, editor.selectedAnnotationIndex]);

  const content = (
    <div
      className={`ife-overlay ${className}`}
      style={{
        '--ife-accent': accentColor,
        display: hidden ? 'none' : 'flex',
      } as React.CSSProperties}
      onClick={e => e.stopPropagation()}
    >
      <div className="ife-modal" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="ife-header">
          <span className="ife-edit-title">Image Feedback Editor</span>
          {exportError && (
            <span className="ife-export-error">
              Export failed. Please try again.
            </span>
          )}
          <div className="ife-header-actions">
            <button
              type="button"
              className="ife-btn ife-btn-ghost"
              onClick={handleCancel}
              title={showCancelConfirm ? 'Click again to discard changes' : 'Cancel'}
            >
              <X size={16} />
              {showCancelConfirm ? 'Discard changes?' : 'Cancel'}
            </button>
            {onDownload && (
              <button
                type="button"
                className="ife-btn ife-btn-secondary"
                onClick={handleDownload}
                disabled={!editor.imageLoaded || editor.isExporting}
                title="Download annotated image"
              >
                {editor.isExporting ? (
                  <>
                    <Loader2 size={16} className="ife-spinner" />
                    Exporting...
                  </>
                ) : (
                  <>
                    <Download size={16} />
                    Download
                  </>
                )}
              </button>
            )}
            <button
              type="button"
              className="ife-btn ife-btn-primary"
              onClick={handleCreateReply}
              disabled={!editor.imageLoaded || editor.isExporting}
              title="Create reply with annotated image"
            >
              {editor.isExporting ? (
                <>
                  <Loader2 size={16} className="ife-spinner" />
                  Exporting...
                </>
              ) : (
                <>
                  <Check size={16} />
                  Create Reply
                </>
              )}
            </button>
          </div>
        </div>

        {/* Body: toolbar + canvas */}
        <div className="ife-modal-body">
          <ImageEditorToolbar
            tool={editor.tool}
            setTool={editor.setTool}
            color={editor.color}
            setColor={editor.setColor}
            lineWidth={editor.lineWidth}
            setLineWidth={editor.setLineWidth}
            opacity={editor.opacity}
            setOpacity={editor.setOpacity}
            fillShape={editor.fillShape}
            setFillShape={editor.setFillShape}
            fontSize={editor.fontSize}
            setFontSize={editor.setFontSize}
            textBold={editor.textBold}
            setTextBold={editor.setTextBold}
            textItalic={editor.textItalic}
            setTextItalic={editor.setTextItalic}
            textHasBackground={editor.textHasBackground}
            setTextHasBackground={editor.setTextHasBackground}
            textBackgroundColor={editor.textBackgroundColor}
            setTextBackgroundColor={editor.setTextBackgroundColor}
            canUndo={editor.canUndo}
            canRedo={editor.canRedo}
            undo={editor.undo}
            redo={editor.redo}
            reset={editor.reset}
            showAnnotations={editor.showAnnotations}
            toggleAnnotations={editor.toggleAnnotations}
            flipHorizontal={editor.flipHorizontal}
            flipVertical={editor.flipVertical}
            zoom={editor.zoom}
            zoomIn={editor.zoomIn}
            zoomOut={editor.zoomOut}
            zoomReset={editor.zoomReset}
            fitToScreen={editor.fitToScreen}
            pinStyle={editor.pinStyle}
            pinValue={editor.pinValue}
            setPinStyle={editor.setPinStyle}
            setPinValue={editor.setPinValue}
            resetPinValue={editor.resetPinValue}
          />

          {/* SVG area */}
          <div className="ife-canvas-area" ref={editor.containerRef}>
            {!editor.imageLoaded && (
              <div className="ife-loading">Loading image…</div>
            )}

            {editor.imageLoaded && (
              <div
                className="ife-canvas-wrapper"
                ref={editor.wrapperRef}
                style={{
                  width: editor.imageWidth * editor.zoom,
                  height: editor.imageHeight * editor.zoom,
                }}
              >
                {/* Background image */}
                <img
                  ref={editor.imgRef}
                  src={editor.imageSrc}
                  alt="Editor background"
                  className="ife-image-bg"
                  crossOrigin={editor.useCrossOrigin ? 'anonymous' : undefined}
                  style={{
                    width: editor.imageWidth * editor.zoom,
                    height: editor.imageHeight * editor.zoom,
                    transform: `scale(${editor.flipState.h ? -1 : 1}, ${editor.flipState.v ? -1 : 1})`,
                  }}
                />

                {/* SVG overlay for annotations */}
                <svg
                  ref={editor.svgRef}
                  className="ife-svg-overlay"
                  viewBox={`0 0 ${editor.imageWidth} ${editor.imageHeight}`}
                  style={{ cursor: svgCursor }}
                  onMouseDown={handleSvgMouseDown}
                  onMouseMove={editor.onMouseMove}
                  onMouseUp={handleSvgMouseUp}
                  onMouseLeave={handleSvgMouseLeave}
                >
                  {/* SVG filter definitions for privacy effects */}
                  <defs>
                    {/* Frosted glass effect with distortion and texture */}
                    <filter id="ife-pixelate" x="-50%" y="-50%" width="200%" height="200%" colorInterpolationFilters="sRGB">
                      {/* Step 1: Generate turbulence for glass texture */}
                      <feTurbulence
                        type="fractalNoise"
                        baseFrequency="0.03"
                        numOctaves="4"
                        result="turbulence"
                        seed="5"
                      />

                      {/* Step 2: Distort the source image (glass warping effect) */}
                      <feDisplacementMap
                        in="SourceGraphic"
                        in2="turbulence"
                        scale="25"
                        xChannelSelector="R"
                        yChannelSelector="G"
                        result="displaced"
                      />

                      {/* Step 3: Apply strong blur for privacy */}
                      <feGaussianBlur
                        in="displaced"
                        stdDeviation="18"
                        result="blurred"
                      />

                      {/* Step 4: Brighten and add frosted effect */}
                      <feComponentTransfer in="blurred" result="brightened">
                        <feFuncR type="linear" slope="1.15" intercept="0.08"/>
                        <feFuncG type="linear" slope="1.15" intercept="0.08"/>
                        <feFuncB type="linear" slope="1.15" intercept="0.08"/>
                      </feComponentTransfer>

                      {/* Step 5: Add white frosted overlay */}
                      <feFlood floodColor="white" floodOpacity="0.3" result="whiteOverlay"/>
                      <feComposite in="brightened" in2="whiteOverlay" operator="over" result="frosted"/>

                      {/* Step 6: Add fine noise for glass texture */}
                      <feTurbulence
                        type="fractalNoise"
                        baseFrequency="0.9"
                        numOctaves="3"
                        result="finnoise"
                      />
                      <feColorMatrix in="finoise" type="saturate" values="0" result="grainoise"/>
                      <feBlend in="frosted" in2="grainoise" mode="overlay" opacity="0.15" result="textured"/>

                      {/* Step 7: Final color adjustment */}
                      <feComponentTransfer in="textured">
                        <feFuncA type="linear" slope="1.2" intercept="0.1"/>
                      </feComponentTransfer>
                    </filter>

                    {/* Simple blur filter for glass effect */}
                    <filter id="blur-glass">
                      <feGaussianBlur stdDeviation="8" />
                      <feComponentTransfer>
                        <feFuncA type="linear" slope="1.1" />
                      </feComponentTransfer>
                    </filter>

                    {/* Pattern for frosted glass texture */}
                    <pattern id="blur-pattern" patternUnits="userSpaceOnUse" width="25" height="25">
                      <rect width="25" height="25" fill="#e8e8e8" />
                      <circle cx="4" cy="4" r="2" fill="white" opacity="0.7" />
                      <circle cx="18" cy="7" r="1.5" fill="white" opacity="0.6" />
                      <circle cx="10" cy="18" r="1.8" fill="white" opacity="0.8" />
                      <circle cx="20" cy="20" r="2.2" fill="#f5f5f5" opacity="0.7" />
                      <circle cx="7" cy="14" r="1.3" fill="#d0d0d0" opacity="0.5" />
                      <circle cx="15" cy="3" r="1.6" fill="white" opacity="0.6" />
                      <circle cx="22" cy="12" r="1.4" fill="#e0e0e0" opacity="0.7" />
                    </pattern>
                  </defs>

                  {/* Render annotations */}
                  {editor.showAnnotations && allAnnotations.map((annotation, idx) => (
                    <AnnotationRenderer
                      key={annotation.id}
                      annotation={annotation}
                      isSelected={idx === editor.selectedAnnotationIndex}
                    />
                  ))}
                </svg>

                {/* Text input overlay */}
                {editor.textInput && (
                  <div
                    className="ife-text-input-wrapper"
                    style={{
                      position: 'absolute',
                      left: `${editor.textInput.screenX}px`,
                      top: `${editor.textInput.screenY}px`,
                      zIndex: 100,
                    }}
                  >
                    <textarea
                      ref={textareaRef}
                      className="ife-text-input"
                      value={textValue}
                      onChange={e => setTextValue(e.target.value)}
                      onKeyDown={handleTextKeyDown}
                      rows={2}
                      placeholder="Type text… Enter to confirm, Esc to cancel"
                      autoFocus
                      style={{
                        fontSize: `${editor.fontSize}px`,
                        fontWeight: editor.textBold ? 'bold' : 'normal',
                        fontStyle: editor.textItalic ? 'italic' : 'normal',
                        ...(editor.textHasBackground && { backgroundColor: editor.textBackgroundColor }),
                      }}
                    />
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  return createPortal(content, document.body);
};

export default ImageFeedbackEditor;
